import React, { useState } from 'react';
import { useFum } from 'hooks/useFum';
import {
  TutorMatriculaResponse,
  PARENTESCO_LABEL,
  TIPO_SANGRE_LABEL,
  TUTOR_VACIO,
  ParentescoTutor,
  TipoSangre,
} from 'types/fum.types';
import { documentoService } from 'services/documentoService';
import { exportarPDF } from 'utils/reporteExport';
import { htmlFum } from 'utils/documentoPdf';
import { reniecService } from 'services/reniecService';

// ============================================================
// FumPage — Ficha Única de Matrícula (FUM)
// Gestión de tutores/apoderados y datos de salud del estudiante
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

interface FumPageProps {
  matriculaId:      number;
  estudianteNombre: string;
  onBack:           () => void;
  /** Si true, oculta la cabecera (modo embedded en wizard) */
  embedded?:        boolean;
}

type Tab = 'tutores' | 'salud';

const PARENTESCO_OPTS: ParentescoTutor[] = ['PADRE', 'MADRE', 'TUTOR', 'APODERADO'];
const TIPO_SANGRE_OPTS: TipoSangre[] = ['A_POS', 'A_NEG', 'B_POS', 'B_NEG', 'O_POS', 'O_NEG', 'AB_POS', 'AB_NEG'];

type TutorForm = Omit<typeof TUTOR_VACIO, never>;

const FORM_VACIO: TutorForm = { ...TUTOR_VACIO };

const FumPage: React.FC<FumPageProps> = ({ matriculaId, estudianteNombre, onBack, embedded = false }) => {
  const {
    tutores, salud, loading, error, saving, saveError, saveSuccess,
    agregarTutor, actualizarTutor, eliminarTutor, guardarSalud,
  } = useFum(matriculaId);

  const [tab, setTab] = useState<Tab>('tutores');
  const [loadingPdf, setLoadingPdf] = useState(false);

  const handleDescargarFum = async () => {
    setLoadingPdf(true);
    try {
      const data = await documentoService.getFum(matriculaId);
      exportarPDF(`FUM — ${estudianteNombre}`, htmlFum(data));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error al generar la FUM');
    } finally {
      setLoadingPdf(false);
    }
  };
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<TutorForm>(FORM_VACIO);
  const [reniecLoading, setReniecLoading] = useState(false);
  const [reniecError,   setReniecError]   = useState<string | null>(null);
  const [reniecOk,      setReniecOk]      = useState(false);

  const handleTutorDniBlur = async () => {
    const dni = form.dni.trim();
    if (dni.length !== 8 || !/^\d{8}$/.test(dni)) return;
    setReniecLoading(true); setReniecError(null); setReniecOk(false);
    try {
      const result = await reniecService.consultarDni(dni);
      if (!result.dniValido) {
        setReniecError('DNI no encontrado o inválido en RENIEC.');
      } else if (result.datos) {
        setForm(p => ({
          ...p,
          nombres:   result.datos!.nombres,
          apellidos: [result.datos!.apellidoPaterno, result.datos!.apellidoMaterno].filter(Boolean).join(' '),
        }));
        setReniecOk(true);
      }
    } catch {
      // RENIEC inaccesible — continuar sin pre-relleno
    } finally {
      setReniecLoading(false);
    }
  };
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  // Salud form
  const [saludForm, setSaludForm] = useState({
    pesoKg: salud?.pesoKg ?? null as number | null,
    tallaCm: salud?.tallaCm ?? null as number | null,
    tipoSangre: salud?.tipoSangre ?? null as TipoSangre | null,
    alergias: salud?.alergias ?? '',
    condicionEspecial: salud?.condicionEspecial ?? '',
    discapacidad: salud?.discapacidad ?? false,
    tipoDiscapacidad: salud?.tipoDiscapacidad ?? '',
    vacunasAlDia: false,
    seguroSalud: salud?.seguroSalud ?? '',
    numeroSeguro: salud?.numeroSeguro ?? '',
  });

  React.useEffect(() => {
    if (salud) {
      setSaludForm({
        pesoKg: salud.pesoKg,
        tallaCm: salud.tallaCm,
        tipoSangre: salud.tipoSangre,
        alergias: salud.alergias ?? '',
        condicionEspecial: salud.condicionEspecial ?? '',
        discapacidad: salud.discapacidad,
        tipoDiscapacidad: salud.tipoDiscapacidad ?? '',
        vacunasAlDia: false,
        seguroSalud: salud.seguroSalud ?? '',
        numeroSeguro: salud.numeroSeguro ?? '',
      });
    }
  }, [salud]);

  const abrirAgregar = () => {
    setForm(FORM_VACIO);
    setEditId(null);
    setShowForm(true);
  };

  const abrirEditar = (t: TutorMatriculaResponse) => {
    setForm({
      parentesco: t.parentesco,
      nombres: t.nombres,
      apellidos: t.apellidos,
      dni: t.dni,
      ocupacion: t.ocupacion,
      celular: t.celular,
      email: t.email,
      viveConEstudiante: t.viveConEstudiante,
      esContactoPrincipal: t.esContactoPrincipal,
    });
    setEditId(t.idTutorMatricula);
    setShowForm(true);
  };

  const handleGuardarTutor = async () => {
    if (editId !== null) {
      await actualizarTutor(editId, form);
    } else {
      await agregarTutor(form);
    }
    setShowForm(false);
    setEditId(null);
  };

  const handleEliminar = async (id: number) => {
    await eliminarTutor(id);
    setConfirmDelete(null);
  };

  const handleGuardarSalud = async () => {
    await guardarSalud({
      pesoKg: saludForm.pesoKg,
      tallaCm: saludForm.tallaCm,
      tipoSangre: saludForm.tipoSangre,
      alergias: saludForm.alergias || null,
      condicionEspecial: saludForm.condicionEspecial || null,
      discapacidad: saludForm.discapacidad,
      tipoDiscapacidad: saludForm.discapacidad ? (saludForm.tipoDiscapacidad || null) : null,
      seguroSalud: saludForm.seguroSalud || null,
      numeroSeguro: saludForm.numeroSeguro || null,
    });
  };

  return (
    <div className="space-y-5">
      {/* Cabecera (oculta en modo embedded) */}
      {!embedded && (
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-200 hover:text-white text-sm mb-3 transition-colors"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"/>
            </svg>
            Volver
          </button>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold">Ficha Única de Matrícula</h1>
              <p className="text-blue-200 mt-1">{estudianteNombre}</p>
            </div>
            <button
              onClick={handleDescargarFum}
              disabled={loadingPdf}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors disabled:opacity-60"
            >
              {loadingPdf ? <SpinnerIcon /> : (
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v8.614l2.955-3.129a.75.75 0 1 1 1.09 1.03l-4.25 4.5a.75.75 0 0 1-1.09 0l-4.25-4.5a.75.75 0 1 1 1.09-1.03L9.25 12.364V3.75A.75.75 0 0 1 10 3ZM3.5 15.75a.75.75 0 0 1 .75-.75h11.5a.75.75 0 0 1 0 1.5H4.25a.75.75 0 0 1-.75-.75Z" clipRule="evenodd"/>
                </svg>
              )}
              Descargar FUM
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        {(['tutores', 'salud'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors
              ${tab === t
                ? 'bg-blue-700 text-white shadow-sm'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
          >
            {t === 'tutores' ? 'Tutores / Apoderados' : 'Datos de Salud'}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex justify-center py-12"><SpinnerIcon /></div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{error}</div>
      )}

      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{saveError}</div>
      )}

      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-green-700 text-sm font-semibold">
          Guardado correctamente
        </div>
      )}

      {/* TAB TUTORES */}
      {!loading && tab === 'tutores' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={abrirAgregar}
              className="bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-xl px-5 py-2.5 transition-colors"
            >
              + Agregar tutor
            </button>
          </div>

          {tutores.length === 0 && !showForm && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-14 text-center">
              <p className="text-slate-400 text-sm">No hay tutores registrados</p>
            </div>
          )}

          {tutores.map(t => (
            <div key={t.idTutorMatricula} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-slate-800">{t.apellidos}, {t.nombres}</p>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
                      {PARENTESCO_LABEL[t.parentesco]}
                    </span>
                    {t.esContactoPrincipal && (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                        Contacto principal
                      </span>
                    )}
                    {t.viveConEstudiante && (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                        Vive con estudiante
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 mt-3 text-sm text-slate-600">
                    <span><strong>DNI:</strong> {t.dni}</span>
                    <span><strong>Celular:</strong> {t.celular}</span>
                    <span><strong>Ocupación:</strong> {t.ocupacion}</span>
                    <span className="col-span-2"><strong>Email:</strong> {t.email}</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => abrirEditar(t)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => setConfirmDelete(t.idTutorMatricula)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-100 hover:bg-red-200 text-red-700 transition-colors"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Formulario inline */}
          {showForm && (
            <div className="bg-white rounded-2xl border border-blue-200 shadow-sm p-6 space-y-4">
              <h3 className="font-bold text-slate-800">{editId ? 'Editar tutor' : 'Agregar tutor'}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Parentesco</label>
                  <select
                    value={form.parentesco}
                    onChange={e => setForm(p => ({ ...p, parentesco: e.target.value as ParentescoTutor }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {PARENTESCO_OPTS.map(o => (
                      <option key={o} value={o}>{PARENTESCO_LABEL[o]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">DNI</label>
                  <input
                    type="text" maxLength={8}
                    value={form.dni}
                    onChange={e => { setForm(p => ({ ...p, dni: e.target.value })); setReniecError(null); setReniecOk(false); }}
                    onBlur={handleTutorDniBlur}
                    className={`w-full px-3 py-2.5 rounded-lg border text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 ${reniecError ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                    placeholder="12345678"
                  />
                  {reniecLoading && <p className="text-xs text-slate-400 mt-1">Consultando RENIEC...</p>}
                  {reniecError  && <p className="text-xs text-red-500 mt-1">{reniecError}</p>}
                  {reniecOk && !reniecLoading && <p className="text-xs text-green-600 mt-1">✓ Datos pre-cargados desde RENIEC</p>}
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nombres</label>
                  <input
                    type="text"
                    value={form.nombres}
                    onChange={e => setForm(p => ({ ...p, nombres: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nombres"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Apellidos</label>
                  <input
                    type="text"
                    value={form.apellidos}
                    onChange={e => setForm(p => ({ ...p, apellidos: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Apellidos"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Ocupación</label>
                  <input
                    type="text"
                    value={form.ocupacion}
                    onChange={e => setForm(p => ({ ...p, ocupacion: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ocupación"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Celular</label>
                  <input
                    type="text" maxLength={9}
                    value={form.celular}
                    onChange={e => setForm(p => ({ ...p, celular: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="987654321"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="correo@ejemplo.com"
                  />
                </div>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.viveConEstudiante}
                    onChange={e => setForm(p => ({ ...p, viveConEstudiante: e.target.checked }))}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Vive con el estudiante</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.esContactoPrincipal}
                    onChange={e => setForm(p => ({ ...p, esContactoPrincipal: e.target.checked }))}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Es contacto principal</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => { setShowForm(false); setEditId(null); }}
                  className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleGuardarTutor}
                  disabled={saving}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors disabled:opacity-60"
                >
                  {saving && <SpinnerIcon />}
                  Guardar
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB SALUD */}
      {!loading && tab === 'salud' && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
          <h3 className="font-bold text-slate-800 text-lg">Datos de Salud</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Peso (kg)</label>
              <input
                type="number" step="0.1"
                value={saludForm.pesoKg ?? ''}
                onChange={e => setSaludForm(p => ({ ...p, pesoKg: e.target.value ? parseFloat(e.target.value) : null }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: 45.5"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Talla (cm)</label>
              <input
                type="number" step="0.1"
                value={saludForm.tallaCm ?? ''}
                onChange={e => setSaludForm(p => ({ ...p, tallaCm: e.target.value ? parseFloat(e.target.value) : null }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: 155"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tipo de Sangre</label>
              <select
                value={saludForm.tipoSangre ?? ''}
                onChange={e => setSaludForm(p => ({ ...p, tipoSangre: (e.target.value || null) as TipoSangre | null }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Sin especificar —</option>
                {TIPO_SANGRE_OPTS.map(o => (
                  <option key={o} value={o}>{TIPO_SANGRE_LABEL[o]}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Alergias</label>
              <textarea
                rows={2}
                value={saludForm.alergias}
                onChange={e => setSaludForm(p => ({ ...p, alergias: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Describe las alergias conocidas..."
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Condición Especial</label>
              <textarea
                rows={2}
                value={saludForm.condicionEspecial}
                onChange={e => setSaludForm(p => ({ ...p, condicionEspecial: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Condiciones médicas relevantes..."
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Seguro de Salud</label>
              <input
                type="text"
                value={saludForm.seguroSalud}
                onChange={e => setSaludForm(p => ({ ...p, seguroSalud: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: SIS, ESSALUD"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Número de Seguro</label>
              <input
                type="text"
                value={saludForm.numeroSeguro}
                onChange={e => setSaludForm(p => ({ ...p, numeroSeguro: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Número de póliza o afiliado"
              />
            </div>
          </div>

          <div className="flex gap-6 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={saludForm.discapacidad}
                onChange={e => setSaludForm(p => ({ ...p, discapacidad: e.target.checked }))}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">Tiene discapacidad</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={saludForm.vacunasAlDia}
                onChange={e => setSaludForm(p => ({ ...p, vacunasAlDia: e.target.checked }))}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">Vacunas al día</span>
            </label>
          </div>

          {saludForm.discapacidad && (
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tipo de Discapacidad</label>
              <textarea
                rows={2}
                value={saludForm.tipoDiscapacidad}
                onChange={e => setSaludForm(p => ({ ...p, tipoDiscapacidad: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Describe el tipo de discapacidad..."
              />
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleGuardarSalud}
              disabled={saving}
              className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-xl px-5 py-2.5 transition-colors disabled:opacity-60"
            >
              {saving && <SpinnerIcon />}
              Guardar Datos de Salud
            </button>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminación */}
      {confirmDelete !== null && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-lg text-slate-800 mb-2">¿Eliminar tutor?</h3>
            <p className="text-slate-500 text-sm mb-5">Esta acción no se puede deshacer.</p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleEliminar(confirmDelete)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60"
              >
                {saving && <SpinnerIcon />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FumPage;
